import { Box, useTheme, useMediaQuery } from '@mui/material';
import {
  Bubble,
  LevelingUpIcon,
  TeamworkIcon,
  WorkLifeBalanceIcon,
  Button,
  KeepingItRealIcon,
  QualityIcon,
  PassionIcon,
} from '@rumblefish/ui/Rumblefish23Theme';
import type { BubbleParams } from '@rumblefish/ui/src/components/Rumblefish23Theme/Bubble/Bubble.types';
import gsap from 'gsap';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { handleScroll } from '../../../utils/handleScroll';
import type { HeroProps } from './Hero.types';
import {
  BubblesStyledStack,
  HeroSection,
  StyledDescription,
  StyledGrid,
  StyledHeroHeading,
  StyledHeroHeadingWrapper,
  StyledIntroStack,
  StyledRowGrid,
  StyledStack,
  StyledTitle,
  TextStyledStack,
  CtaContainer,
} from './styles';

interface BubblesGridProps {
  iconsArr: (BubbleParams | null)[][];
  isLeft?: boolean;
}

const BubblesGrid: React.FC<BubblesGridProps> = ({ iconsArr, isLeft }) => {
  return (
    <BubblesStyledStack isLeft={isLeft}>
      {iconsArr.map((row, index) => {
        return (
          <StyledGrid key={index} container>
            {row.map((el, rowIndex) => {
              return (
                <StyledRowGrid key={rowIndex} item>
                  {el && <Bubble params={el} />}
                </StyledRowGrid>
              );
            })}
          </StyledGrid>
        );
      })}
    </BubblesStyledStack>
  );
};

export const Hero = ({ ...props }: HeroProps) => {
  const theme = useTheme();
  const olRef = useRef<HTMLOListElement>(null);
  const offersRef = useRef<HTMLDivElement>(null);
  const [scrollToEl, setScrollToEl] = useState<HTMLElement | null>(null);
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const targetScrollToEl = props.hasOffers
      ? document.getElementById('current-openings')
      : document.getElementById('current-openings-empty');
    targetScrollToEl && setScrollToEl(targetScrollToEl);
  }, [props.hasOffers]);

  const services = [
    'Join',
    'Grow with',
    'Shine with',
    'Belong to',
    'Achieve in',
    'Create with',
    'Be a part of',
    'Step Into',
  ];

  const createLeftRow1 = useMemo(
    () => [
      null,
      null,
      {
        name: 'heroLevelingUpIcon',
        icon: LevelingUpIcon,
        size: mdDown ? 70 : 100,
        top: mdDown ? -60 : -40,
        left: 0,
        boxShadow: theme.mixins.grapeGradientTransparent?.boxShadow || '',
        floatingY: -6.8,
        duration: 3.5,
      },
      {
        name: 'heroTeamWorkIcon',
        icon: TeamworkIcon,
        size: 90,
        top: -50,
        left: 100,
        boxShadow: theme.mixins.violetGradientTransparent?.boxShadow || '',
        floatingY: -4.7,
        duration: 2.5,
      },
    ],
    [
      mdDown,
      theme.mixins.grapeGradientTransparent?.boxShadow,
      theme.mixins.violetGradientTransparent?.boxShadow,
    ]
  );
  const createLeftRow2 = useMemo(
    () => [
      {
        name: 'heroQualityIcon',
        icon: QualityIcon,
        top: mdDown ? -130 : -50,
        left: mdDown ? 70 : 30,
        size: mdDown ? 70 : 95,
        boxShadow: theme.mixins.aquamarineGradientTransparent?.boxShadow || '',
        floatingY: -2.9,
        duration: 2.9,
      },
      null,
      {
        name: 'heroWorkLifeBalanceIcon',
        icon: WorkLifeBalanceIcon,
        top: mdDown ? 0 : -30,
        left: mdDown ? -130 : 60,
        size: mdDown ? 70 : 100,
        boxShadow: theme.mixins.magentaGradientTransparent?.boxShadow || '',
        floatingY: 7.4,
        duration: 4,
      },
      {
        name: 'heroKeepingItRealIcon',
        icon: KeepingItRealIcon,
        top: 0,
        left: mdDown ? 40 : 140,
        size: 85,
        boxShadow: theme.mixins.purpleGradientTransparent?.boxShadow || '',
        floatingY: -5.6,
        duration: 2.4,
      },
    ],
    [
      mdDown,
      theme.mixins.aquamarineGradientTransparent?.boxShadow,
      theme.mixins.magentaGradientTransparent?.boxShadow,
      theme.mixins.purpleGradientTransparent?.boxShadow,
    ]
  );
  const createLeftRow3 = useMemo(
    () => [
      null,
      null,
      {
        name: 'heroPassionIcon',
        icon: PassionIcon,
        top: mdDown ? 60 : -30,
        left: mdDown ? -150 : 0,
        size: 85,
        boxShadow: theme.mixins.purpleGradientTransparent?.boxShadow || '',
        floatingY: 5.6,
        duration: 2.4,
      },
      {
        name: 'heroQualityIcon',
        icon: QualityIcon,
        top: mdDown ? 80 : 30,
        left: 80,
        size: 85,
        boxShadow: theme.mixins.cyanGradientTransparent?.boxShadow || '',
        floatingY: 7.6,
        duration: 3.4,
      },
    ],
    [
      mdDown,
      theme.mixins.purpleGradientTransparent?.boxShadow,
      theme.mixins.cyanGradientTransparent?.boxShadow,
    ]
  );
  const createRightRow1 = useMemo(
    () => [
      null,
      {
        name: 'heroKeepingItRealIcon',
        icon: KeepingItRealIcon,
        top: 0,
        left: -100,
        size: 70,
        boxShadow: theme.mixins.redGradientTransparent?.boxShadow || '',
        floatingY: 7,
        duration: 2.3,
      },
      null,
      {
        name: 'heroPassionIcon',
        icon: PassionIcon,
        top: -30,
        left: -100,
        boxShadow: theme.mixins.grapeGradientTransparent?.boxShadow || '',
        floatingY: -6.8,
        size: 90,
        duration: 3.5,
      },
    ],
    [
      theme.mixins.redGradientTransparent?.boxShadow,
      theme.mixins.grapeGradientTransparent?.boxShadow,
    ]
  );
  const createRightRow2 = useMemo(
    () => [
      null,
      null,
      null,
      {
        name: 'heroQualityIcon',
        icon: QualityIcon,
        top: -40,
        left: -90,
        size: 115,
        boxShadow: theme.mixins.blueGradientTransparent?.boxShadow || '',
        floatingY: -8,
        duration: 3,
      },
    ],
    [theme.mixins.blueGradientTransparent?.boxShadow]
  );
  const createRightRow3 = useMemo(
    () => [
      null,
      null,
      {
        name: 'heroWorkLifeBalanceIcon',
        icon: WorkLifeBalanceIcon,
        top: -100,
        left: -160,
        size: 90,
        boxShadow: theme.mixins.aquamarineGradientTransparent?.boxShadow || '',
        floatingY: -2.9,
        duration: 2.9,
      },
    ],
    [theme.mixins.aquamarineGradientTransparent?.boxShadow]
  );
  const createRightRow4 = useMemo(
    () => [
      null,
      {
        name: 'heroPassionIcon',
        icon: PassionIcon,
        top: -60,
        left: -100,
        size: 75,
        boxShadow: theme.mixins.purpleGradientTransparent?.boxShadow || '',
        floatingY: 5.6,
        duration: 2.4,
      },
      {
        name: 'heroTeamWorkIcon',
        icon: TeamworkIcon,
        top: -100,
        left: -20,
        size: 80,
        boxShadow: theme.mixins.magentaGradientTransparent?.boxShadow || '',
        floatingY: 7.4,
        duration: 4,
      },
      {
        name: 'heroLevelingUpIcon',
        icon: LevelingUpIcon,
        top: -200,
        left: 0,
        size: 85,
        boxShadow: theme.mixins.cyanGradientTransparent?.boxShadow || '',
        floatingY: 7.6,
        duration: 3.4,
      },
    ],
    [
      theme.mixins.purpleGradientTransparent?.boxShadow,
      theme.mixins.magentaGradientTransparent?.boxShadow,
      theme.mixins.cyanGradientTransparent?.boxShadow,
    ]
  );

  const iconsArrLeft: (BubbleParams | null)[][] = useMemo(() => {
    return [createLeftRow1, createLeftRow2, createLeftRow3];
  }, [createLeftRow1, createLeftRow2, createLeftRow3]);
  const iconsArrRight: (BubbleParams | null)[][] = useMemo(() => {
    return [createRightRow1, createRightRow2, createRightRow3, createRightRow4];
  }, [createRightRow1, createRightRow2, createRightRow3, createRightRow4]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (olRef !== null && olRef.current !== null) {
        const spanTimeline = gsap.timeline({
          repeat: -1,
          repeatDelay: 1,
        });

        const serviceItems = olRef.current.childNodes;
        serviceItems.forEach((_word, index) => {
          spanTimeline.fromTo(
            'ol',
            {
              '--word-counter': index - 1,
            },
            {
              '--word-counter': index,
              duration: 0.8,
              ease: 'elastic.out(0.4, 3)',
              delay: index === 0 ? 0 : 0.8,
            }
          );
        });
      }
    });
    return () => ctx.revert();
  }, [olRef]);

  return (
    <>
      <HeroSection width="normal" borderBottomOn>
        <StyledStack {...props}>
          <StyledIntroStack>
            <BubblesGrid iconsArr={iconsArrLeft} isLeft={true} />
            <TextStyledStack>
              <StyledTitle variant="display_md">
                <StyledHeroHeadingWrapper variant="display_md">
                  <ol ref={olRef}>
                    {services.map((service, index) => {
                      return (
                        <li key={index} className="service-item">
                          <StyledHeroHeading variant="display_md">
                            {service}
                          </StyledHeroHeading>
                        </li>
                      );
                    })}
                  </ol>
                </StyledHeroHeadingWrapper>
                Our team
              </StyledTitle>
              <StyledDescription paragraph variant="regular_500">
                Join the team of top-tier software
                <br />
                development specialists!
              </StyledDescription>
            </TextStyledStack>
            <BubblesGrid iconsArr={iconsArrRight} />
          </StyledIntroStack>

          <CtaContainer>
            <Button
              secondaryVariant={true}
              onClick={() => {
                handleScroll(offersRef);
              }}>
              {props.hasOffers ? 'View open positions' : 'Join us'}
            </Button>
          </CtaContainer>
        </StyledStack>
      </HeroSection>
      {scrollToEl &&
        createPortal(
          <Box
            ref={offersRef}
            sx={() => ({
              position: 'absolute',
              top: `calc(-${theme.spacing(4)} - 64px)`,
              left: '0',
              width: '100%',
              zIndex: -1,
            })}
          />,
          scrollToEl
        )}
    </>
  );
};
